import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart3, Loader2, LogOut, LayoutDashboard, Users, Monitor, Megaphone, Calendar, CheckSquare, CreditCard, Download, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

export default function AdvertisingProofOfPlay() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState("all");

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (user) { fetchLogs(); fetchCampaigns(); } }, [user, campaignFilter]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) { navigate("/admin/login"); }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (campaignFilter !== "all") params.append("campaign_id", campaignFilter);
      const response = await axios.get(`${API}/advertising/proof-of-play?${params}`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally { setLoading(false); }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/advertising/campaigns?status=active`, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (<div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>);
  }

  const totalImpressions = logs.reduce((sum, l) => sum + (l.impressions || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 flex">
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800"><ParkWiseLogo size="md" /><p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p></div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (<button key={item.path} onClick={() => navigate(item.path)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? "bg-blue-600/20 text-blue-400" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}><item.icon className="w-5 h-5" /><span className="text-sm font-medium">{item.label}</span></button>);
          })}
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button onClick={() => navigate("/admin/dashboard")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800"><LayoutDashboard className="w-5 h-5" /><span className="text-sm">← Back to Main Admin</span></button>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2"><ParkWiseLogo size="sm" /><div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-slate-500 truncate">{user?.email}</p></div></div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2"><LogOut className="w-4 h-4" /><span>Sign out</span></button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div><h1 className="text-2xl font-bold text-white">Proof of Play</h1><p className="text-slate-400 mt-1">Impression tracking and delivery reports</p></div>
            <Button variant="outline" className="border-slate-700 text-slate-300"><Download className="w-4 h-4 mr-2" />Export Report</Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Total Impressions</p>
                <p className="text-2xl font-bold text-white">{totalImpressions.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Log Entries</p>
                <p className="text-2xl font-bold text-white">{logs.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="pt-6">
                <p className="text-sm text-slate-400">Active Campaigns</p>
                <p className="text-2xl font-bold text-green-400">{campaigns.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="mb-6 flex items-center gap-4">
            <Select value={campaignFilter} onValueChange={setCampaignFilter}>
              <SelectTrigger className="w-64 bg-slate-900 border-slate-700 text-white">
                <SelectValue placeholder="Filter by campaign" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Campaigns</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Logs Table */}
          <Card className="bg-slate-900 border-slate-800">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>
            ) : logs.length === 0 ? (
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No proof of play logs found.</p>
              </CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Campaign</TableHead>
                    <TableHead className="text-slate-400">Screen</TableHead>
                    <TableHead className="text-slate-400">Impressions</TableHead>
                    <TableHead className="text-slate-400">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.log_id} className="border-slate-800 hover:bg-slate-800/50">
                      <TableCell className="text-slate-300">{log.date}</TableCell>
                      <TableCell className="text-white">{log.campaign_id}</TableCell>
                      <TableCell className="text-slate-400">{log.screen_id}</TableCell>
                      <TableCell className="text-emerald-400 font-medium">{log.impressions?.toLocaleString()}</TableCell>
                      <TableCell className="text-slate-400">{log.duration_seconds}s</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
